import React from 'react';
import { NavLink } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import { useDispatch, useSelector } from 'react-redux';
import { createUseStyles } from 'react-jss';
import {
  Pane,
  Strong, Table, Text, toaster,
} from 'evergreen-ui';
import { format, isAfter, isBefore } from 'date-fns';
import { fgbuRegions, cgmsByFgbuId } from 'Services/api/regionsApi';
import {
  stationsFilter4PropsSelector, stationsSetFilter4,
} from 'Features/App/appSlice';
import { VirtualList } from 'Utils/VirtualList';
import SuspenseSpinner from 'Utils/SuspenseSpinner';
import { useLazyGetStationsWithDatasourceProgramBlocksQuery } from 'Services/api/stantionsApi';
import { DATE_FORMATS } from 'Utils/formats';
import { SearchForm } from './Devices.Devices.SearchForm';

const useStyles = createUseStyles({
  rowWrapperHoverable: {
    display: 'grid',
    alignItems: 'center',
    borderBottom: '1px solid rgb(237,240,242)',
    boxSizing: 'border-box',
    height: 'inherit',
    cursor: 'pointer',
    transition: 'all 0.1s linear',
  },

  rowStationsHoverable: {
    padding: '1em',
    backgroundColor: '#F1FAF5',
    '&:hover': {
      backgroundColor: '#efefef',
    },
  },
  rowDatasourcesHoverable: {
    padding: '.5em',
    backgroundColor: '#FEF6F6',
    '&:hover': {
      backgroundColor: 'rgba(67, 90, 111, 0.04)',
    },
  },
  rowDevicesHoverable: {
    padding: '.5em',
    textDecoration: 'none',
    '&:hover': {
      backgroundColor: 'rgba(67, 90, 111, 0.04)',
    },
  },
  devicesWrapper: {
    display: 'grid',
    gridTemplateRows: 'max-content 1fr',
    rowGap: '.5em',
    height: '100%',
  },
  tableWrapper: {
    display: 'grid',
    gridTemplateRows: 'max-content 1fr',
    border: '1px solid #E4E7EB',
    overflowX: 'auto',
    overflowY: 'hidden',
  },
});

const sizes = [10, 25, 50, 100];

const initialParamsState = {
  size: sizes[0],
  page: 0,
  totalPages: 1,
  sort: 'desc',
};

function DevicesDevices() {
  const classes = useStyles();
  const [loadDevices, { data: devices, isFetching: isFetchingDevices }] = useLazyGetStationsWithDatasourceProgramBlocksQuery();

  // заголовки для отображения таблицы в копии, тк отображать их нужно по клику. Есть обязательные колонки
  const [copyTableHeaders, setCopyTableHeaders] = React.useState({
    'Заводской номер': {
      serialNumber: 'serialNumber',
      deviceatt: 'serialNumber',
    },
    'Дата поверки': {
      deviceatt: 'dateVerificationUTC',
      dateVerification: 'dateVerificationUTC',
    },
  });
  const [tableHeaders, setTableHeaders] = React.useState({});
  const [changed, setChanged] = React.useState(false);
  const [params, setParams] = React.useState({
    size: initialParamsState.size,
    enabled: true,
  });
  const dispatch = useDispatch();

  // изменение чекбокса
  const onChangeChecked = React.useCallback((e) => {
    const { checked } = e.target;
    setChanged(true);
    setParams((cur) => ({
      ...cur, enabled: checked,
    }));
  }, []);

  // ФГБУ и ЦГМС
  const filterSelector = useSelector(stationsFilter4PropsSelector);
  const fgbu = useSelector(fgbuRegions);
  const cgms = useSelector(cgmsByFgbuId(filterSelector.fgbu?.id));

  /**
   * Используется во всех onChange
   * Установка заголовков для таблицы.
   * Если в объекте есть только deviceatt, то св-во все равно удаляем
   */
  const setHeaders = React.useCallback((headername, name, value, deviceatt) => {
    setCopyTableHeaders((cur) => {
      const tmp = JSON.parse(JSON.stringify(cur));
      if (value) {
        tmp[headername] = {
          ...tmp[headername],
          [name]: value,
          deviceatt,

        };
      } else if (tmp[headername]?.deviceatt !== 'serialNumber' && tmp[headername]?.deviceatt !== 'dateVerificationUTC') {
        delete tmp[headername]?.[name];
      }
      const obj = tmp[headername] ?? {};
      const objKeys = Object.keys(obj);
      if (!objKeys.length || (objKeys.length === 1 && objKeys[0] === 'deviceatt')) delete tmp[headername];
      return { ...tmp };
    });
  }, []);
  /** Параметры */
  const onChangeParams = React.useCallback((e) => {
    setChanged(true);
    const { name, value } = e.target;
    const headername = e.target.getAttribute('headername');
    const deviceatt = e.target.getAttribute('deviceatt');
    if (headername) setHeaders(headername, name, value, deviceatt);
    setParams((cur) => {
      if (value) {
        return {
          ...cur,
          [name]: value,
        };
      }
      const newObj = JSON.parse(JSON.stringify(cur));
      if (Object.prototype.hasOwnProperty.call(newObj, name)) delete newObj[name];
      return newObj;
    });
  }, [setHeaders]);

  /** ФГБУ ЦГМС */
  const onChange = React.useCallback((e) => {
    // const { name, value } = e.target;
    setChanged(true);
    if (e) {
      const name = e.vocRegionType.id === 1 ? 'fgbu' : 'cgms';
      const data = { fgbu, cgms }[name].find(({ id }) => id === parseInt(e.id, 10));
      dispatch(stationsSetFilter4({ [name]: data }));
      if (name === 'fgbu') dispatch(stationsSetFilter4({ cgms: null }));
    }
  }, [cgms, dispatch, fgbu]);

  /** Даты */
  const onChangeDate = React.useCallback((e, type, startDate, endDate) => {
    setChanged(true);
    const { value, name } = e.target;
    const headername = e.target.getAttribute('headername');
    const deviceatt = e.target.getAttribute('deviceatt');
    if (headername) setHeaders(headername, name, value, deviceatt);

    setParams((d) => {
      if (value) {
        if (type === 'start' && isAfter(new Date(value), new Date(d[endDate]))) {
          toaster.danger('Время начала должно быть меньше конечного', { id: name });
          return d;
        }
        if (name === 'endDate' && isBefore(new Date(value), new Date(d[startDate]))) {
          toaster.danger('Время конца должно быть больше начального', { id: name });
          return d;
        }
        return { ...d, [name]: format(new Date(value), DATE_FORMATS.api) };
      }
      const newObj = JSON.parse(JSON.stringify((d)));
      if (Object.prototype.hasOwnProperty.call(newObj, name)) delete newObj[name];
      return newObj;
    });
  }, [setHeaders]);

  /** Поиск станций с датчиками */
  const fetchMessages = React.useCallback(async (curParams) => {
    setChanged(false);
    setTableHeaders(copyTableHeaders);
    loadDevices(curParams);
  }, [copyTableHeaders, loadDevices]);

  /** onClick на кнопку */
  const searchMessages = React.useCallback((page = 0) => {
    fetchMessages({
      ...params,
      page: +page || 0,
      sort: 'desc',
      programTypeId: 2, // датчики
      // ...(programType.id && { programTypeId: programType.id }),
      ...((filterSelector?.cgms || filterSelector?.fgbu) && { regionId: filterSelector?.cgms?.id || filterSelector?.fgbu?.id }),
    });
  }, [fetchMessages, filterSelector, params]);

  /** Данные в таблице */

  return (
    <div className={classes.devicesWrapper}>
      <SearchForm
        classes={classes}
        params={params}
        sizes={sizes}
        isFetchMessages={isFetchingDevices}
        onChange={onChange}
        onChangeDate={onChangeDate}
        searchMessages={searchMessages}
        onChangeChecked={onChangeChecked}
        onChangeParams={onChangeParams}
        changed={changed}
      />
      <Pane className={classes.tableWrapper}>
        <Table>
          <Table.Head height="auto" minHeight={40}>
            <Table.HeaderCell minWidth={250}><Strong>Станция/Датчик</Strong></Table.HeaderCell>
            {
              Object.keys(tableHeaders).map((head) => (
                <Table.HeaderCell justifyContent="center" key={head} minWidth={250}>
                  <Strong>{head}</Strong>
                </Table.HeaderCell>
              ))
            }
          </Table.Head>
        </Table>
        <MessagesList messages={devices?.content || []} tableHeaders={tableHeaders} isLoading={isFetchingDevices} />
      </Pane>
      {
        devices?.totalPages > 1
          ? (
            <ReactPaginate
              forcePage={devices?.number || 0}
              previousLabel="пред."
              nextLabel="след."
              breakLabel="..."
              breakClassName="break-me"
              pageCount={devices?.totalPages || 1}
              marginPagesDisplayed={2}
              pageRangeDisplayed={5}
              onPageChange={({ selected }) => searchMessages(selected)}
              containerClassName="pagination"
              pageLinkClassName="pageLink"
              subContainerClassName="pages pagination"
              activeClassName="active"
            />
          )
          : null
      }
    </div>
  );
}

const MessagesList = React.memo(({ messages, isLoading, tableHeaders }) => {
  const classes = useStyles();

  /** Данные в таблице */
  const RowTable = React.useCallback((station) => (
    <Pane display="grid" gridAutoFlow="row" gridAutoRows="max-content">
      <Pane className={`${classes.rowStationsHoverable} ${classes.rowWrapperHoverable}`}>
        <Strong color="rgb(16, 112, 202)">{`[${station.idx}] ${station.name}`}</Strong>
      </Pane>
      {
        React.Children.toArray(
          station.datasources?.map((datasource) => (
            <Pane key={datasource.id} display="grid" gridAutoFlow="row" gridAutoRows="max-content">
              <Pane className={`${classes.rowDatasourcesHoverable} ${classes.rowWrapperHoverable}`}>
                <Text>{datasource.datasourceType?.fullName}</Text>
              </Pane>
              {
                React.Children.toArray(
                  datasource.datasourcePrograms.map((dp) => dp.datasourceProgramBlocks.map((dpb) => (
                    <Table.Row
                      isSelectable
                      key={dpb.id}
                      className={`${classes.rowDevicesHoverable} `}
                      is={NavLink}
                      height="auto"
                      minHeight={40}
                      to={`/stations/${station.id}/datasources/${datasource.id}/devices/device-detector/${dpb.id}`}
                      target="_blank"
                    >
                      <Table.TextCell>
                        {dpb.programBlock.deviceType?.fullName || ''}
                      </Table.TextCell>
                      {
                        React.Children.toArray(
                          Object.keys(tableHeaders).map((key) => (
                            <>
                              <Table.TextCell textAlign="center">
                                {
                                  tableHeaders[key].deviceatt.toLowerCase().indexOf('date') > -1 && dpb[tableHeaders[key].deviceatt]
                                      ? format(new Date(dpb[tableHeaders[key].deviceatt]), 'yyyy-MM-dd HH:mm')
                                      : dpb[tableHeaders[key].deviceatt]
                                }
                              </Table.TextCell>
                            </>
                          )),
                        )
                      }
                    </Table.Row>
                  ))),
                )
              }
            </Pane>
          )),
        )
      }
    </Pane>
  ), [classes, tableHeaders]);
  if (isLoading) return <SuspenseSpinner />;
  if (!messages.length) return <Table.Row><Table.Cell><Text>Нет датчиков</Text></Table.Cell></Table.Row>;
  return <VirtualList data={messages} row={RowTable} direction="horizontal" />;
});

MessagesList.displayName = 'MessagesList';

export default DevicesDevices;
